/**
 * University Page Component
 * Main page for learning courses and educational content
 */

import { Flex } from "@chakra-ui/react";
import { PageRouteHeadline } from "@/components/Layout/PageRouteHeadline";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { strategiesMainCardClassName } from "../Strategies/strategiesCardShell";
import { universityApi } from "../../../services/universityApi";
import type { Course as BackendCourse } from "../../../types/university";
import type { UniversityCourseCardModel } from "./UniversityCourseCard";
import { UniversityCoursesTabs, type UniversityCoursesTab } from "./UniversityCoursesTabs";
import { UniversityHeroBanner } from "./UniversityHeroBanner";
import { resolveUniversityCourseThumbnail } from "./universityCourseThumbnails";

interface Course extends UniversityCourseCardModel {
  instructor: string;
  duration?: string;
  difficulty?: "Beginner" | "Intermediate" | "Advanced";
  rating?: number;
  reviews?: number;
  category?: "trading" | "selfImprovement" | "health";
}

const University: React.FC = () => {
  const history = useHistory();
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const FlexAny = Flex as any;

  const [remoteCourses, setRemoteCourses] = useState<Course[]>([]);
  const [remoteEnrolledCourses, setRemoteEnrolledCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState<boolean>(true);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [coursesTab, setCoursesTab] = useState<UniversityCoursesTab>("enrolled");

  const openCourse = useCallback(
    (sanityId: string, courseTitle?: string) => {
      history.push(`/university/course/${sanityId}`, { courseTitle });
    },
    [history],
  );

  const mapBackendCourseToUi = (course: BackendCourse, index: number): Course => {
    const modules = course.modules ?? [];
    const lessonsCount = modules.reduce(
      (sum, module) => sum + (module.lessons ? module.lessons.length : 0),
      0,
    );

    const difficulty =
      course.difficulty && typeof course.difficulty === "string"
        ? ((course.difficulty.charAt(0).toUpperCase() + course.difficulty.slice(1)) as Course["difficulty"])
        : undefined;

    return {
      id: index + 1,
      sanityId: course._id,
      title: course.title,
      instructor: "Tessera University",
      thumbnailGradient: "linear-gradient(135deg, #38BBD1 0%, #3EEAEC 100%)",
      thumbnail: resolveUniversityCourseThumbnail(course),
      duration: course.estimatedDuration ? `${Math.round(course.estimatedDuration / 60)} hours` : "Self-paced",
      difficulty,
      rating: undefined,
      reviews: undefined,
      modules: modules.length,
      lessons: lessonsCount,
      category: "selfImprovement",
      description: course.shortDescription || course.description,
      progress: course.progress?.completion_percentage,
    };
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCourses = async () => {
      setCoursesLoading(true);
      setCoursesError(null);
      try {
        const allCoursesResponse = await universityApi.getCourses();
        if (!isMounted) return;

        const seenSlugs = new Set<string>();
        const uniquePublishedCourses = allCoursesResponse
          .filter((course) => course.isPublished !== false)
          .sort((a, b) => {
            const aDate = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
            const bDate = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
            if (bDate !== aDate) return bDate - aDate;
            return a._id.localeCompare(b._id);
          })
          .filter((course) => {
            const slugValue = typeof course.slug === "string" ? course.slug : course.slug?.current;
            if (!slugValue) return true;
            if (seenSlugs.has(slugValue)) return false;
            seenSlugs.add(slugValue);
            return true;
          });

        setRemoteCourses(uniquePublishedCourses.map((course, index) => mapBackendCourseToUi(course, index)));

        try {
          const enrolledCoursesResponse = await universityApi.getEnrolledCourses();
          if (!isMounted) return;
          setRemoteEnrolledCourses(
            enrolledCoursesResponse.map((course, index) => mapBackendCourseToUi(course, index)),
          );
          setCoursesTab(enrolledCoursesResponse.length > 0 ? "enrolled" : "all");
        } catch {
          if (!isMounted) return;
          setRemoteEnrolledCourses([]);
          setCoursesTab("all");
        }
      } catch (err) {
        if (!isMounted) return;
        setCoursesError(err instanceof Error ? err.message : "Failed to load courses. Please try again.");
      } finally {
        if (isMounted) {
          setCoursesLoading(false);
        }
      }
    };

    void fetchCourses();

    return () => {
      isMounted = false;
    };
  }, []);

  const allCourses = remoteCourses;
  const enrolledCourses = remoteEnrolledCourses;
  const completedCourses = useMemo(
    () =>
      enrolledCourses.filter((course) => typeof course.progress === "number" && course.progress >= 100),
    [enrolledCourses],
  );

  const mainFlexProps: Record<string, unknown> = {
    direction: "column",
    pt: shadcnInset ? 0 : { base: "100px", md: "80px", lg: "40px" },
    pb: "24px",
    px: shadcnInset ? 0 : { base: "20px", xl: "40px" },
    gap: "24px",
    w: "100%",
    minW: 0,
    minH: pageMinHeight,
  };

  return (
    <FlexAny data-university-page="" {...mainFlexProps}>
      {!shadcnInset ? <PageRouteHeadline>University</PageRouteHeadline> : null}

      {coursesLoading ? (
        <FlexAny flex="1" minH={0} w="100%" align="center" justify="center">
          <StatisticsEmptyState
            variant="loading"
            fillParent
            title="Loading courses"
            description="Please wait while we load your course catalog."
          />
        </FlexAny>
      ) : (
        <>
          <UniversityHeroBanner />

          <div data-university-main-card="" className={strategiesMainCardClassName}>
            <UniversityCoursesTabs
              enrolledCourses={enrolledCourses}
              allCourses={allCourses}
              completedCourses={completedCourses}
              coursesTab={coursesTab}
              onCoursesTabChange={setCoursesTab}
              coursesError={coursesError}
              onOpenCourse={openCourse}
            />
          </div>
        </>
      )}
    </FlexAny>
  );
};

export default University;
